import { create } from 'zustand';
import * as mattersApi from './mattersApi';
import { getApiErrorMessage } from './api';
import type { PartyPayload } from './mattersApi';

/**
 * Matter-level access control.
 */
export type MatterAccess = 'normal' | 'restricted' | 'private';

/**
 * Which side of the dispute this barrister represents.
 */
export type MatterSide =
  | 'claimant' | 'defendant'
  | 'applicant' | 'respondent'
  | 'prosecution' | 'defence'
  | 'petitioner' | 'respondent_family'
  | 'trustee' | 'beneficiary'
  | 'neutral';

export interface MatterParty {
  name: string;
  role: 'claimant' | 'defendant' | 'applicant' | 'respondent' | 'prosecution' | 'defence' | 'third_party' | 'intervener' | 'petitioner' | 'beneficiary' | 'trustee';
  contactId?: string;
  solicitorFirm?: string;
}

export interface Matter {
  id: string;
  number: string;
  name: string;
  court: string;
  courtId?: string | null;
  barrister: string;
  /** Barrister-record UUID of the lead barrister — used to reassign via the API
   *  (the API keys on barrister id, not user id). */
  leadBarristerId?: string | null;
  /** User UUID of the lead barrister — use this for identity checks, not the
   *  barrister string which is a formatted display name and varies by rank. */
  leadBarristerUserId?: string | null;
  /** User UUID of the junior barrister (if any) — use this for identity checks. */
  juniorBarristerUserId?: string | null;
  /** Raw ISO date of the next upcoming hearing — use for urgency calculation. */
  nextHearingIso?: string | null;
  status: string;
  next: string;
  area: string;
  practiceAreaId?: string | null;
  subArea: string;
  courtroom: string;
  judge: string;
  judgeId?: string | null;
  feeType: string;
  feeEstimate: number;
  riskRating: string;
  summary: string;
  internalNotes: string;
  role: string;
  clerkUserId?: string | null;
  isHistoricalMatter?: boolean;
  originalFilingDate?: string | null;
  caseStage?: string | null;
  previousAdvocateName?: string | null;
  previousAdvocateFirm?: string | null;
  createdAt: number;
  createdBy?: string;
  updatedBy?: string;
  updatedAt?: number;
  access?: MatterAccess;
  allowedNames?: string[];
  visibility?: 'personal' | 'team' | 'chambers' | 'restricted';
  clerkTeam?: string[];
  side?: MatterSide;
  parties?: MatterParty[];
  disputeRef?: string;
}

/** Pipeline stages in order */
export const PIPELINE_STAGES = [
  { key: 'intake',             label: 'Intake',           color: 'bg-slate-500',   lightBg: 'bg-slate-50',   border: 'border-slate-200' },
  { key: 'active',             label: 'Active',           color: 'bg-emerald-500', lightBg: 'bg-emerald-50', border: 'border-emerald-200' },
  { key: 'listed',             label: 'Hearing Listed',   color: 'bg-brand-500',   lightBg: 'bg-brand-50',   border: 'border-brand-200' },
  { key: 'in_trial',           label: 'In Trial',         color: 'bg-amber-500',   lightBg: 'bg-amber-50',   border: 'border-amber-200' },
  { key: 'judgment_reserved',  label: 'Judgment',         color: 'bg-violet-500',  lightBg: 'bg-violet-50',  border: 'border-violet-200' },
  { key: 'closed_won',         label: 'Won',              color: 'bg-emerald-600', lightBg: 'bg-emerald-50', border: 'border-emerald-300' },
  { key: 'closed_settled',     label: 'Settled',          color: 'bg-slate-400',   lightBg: 'bg-slate-50',   border: 'border-slate-200' },
  { key: 'closed_lost',        label: 'Lost',             color: 'bg-red-500',     lightBg: 'bg-red-50',     border: 'border-red-200' },
] as const;

export const PIPELINE_STAGE_MAP = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, s])) as Record<string, typeof PIPELINE_STAGES[number]>;

/** Extra fields that don't live on the Matter model but travel to the API. */
export interface AddMatterExtras {
  partyA?: PartyPayload;
  partyB?: PartyPayload;
  instructingSolicitorId?: string;
  instructingSolicitor?: { contactId?: string; name?: string; phone?: string; altPhone?: string; email?: string };
  leadBarristerId?: string;
  juniorBarristerId?: string;
  courtId?: string;
  practiceAreaId?: string;
  /** API enum: civil | criminal | family | commercial | chancery | employment |
   *  public_law | immigration | tax | property | personal_injury |
   *  clinical_negligence | inquest | arbitration | other
   *
   *  REQUIRED. The API rejects a missing case type; defaulting it here used to file
   *  unanswered matters as 'other' without telling anyone. The caller validates it. */
  caseType: string;
  /** Court-assigned case reference (e.g. CL-2024-001234) — stored separately
   *  from the chambers-generated matter number. */
  courtCaseNumber?: string;
  /** API enum: low | normal | high | urgent */
  priority?: string;
  isHistoricalMatter?: boolean;
  originalFilingDate?: string;
  caseStage?: string;
  previousAdvocateName?: string;
  previousAdvocateFirm?: string;
  /** Proceed despite a critical conflict (silk/admin only — the server refuses anyone else). */
  conflictOverrideReason?: string;
  onSuccess?: (matter: Matter) => void;
  onError?: (err: { status?: number; message: string }) => void;
}

interface MattersState {
  matters: Matter[];
  loading: boolean;
  error: string | null;
  /** True when the server returned exactly MATTERS_FETCH_LIMIT results — more may exist */
  truncated: boolean;
  /** Fetch all matters from the API (call on page mount) */
  fetchMatters: () => Promise<void>;
  /** Add a matter via API */
  /** `extras` is required — it carries `caseType`, which the API will not accept as absent. */
  addMatter: (input: Omit<Matter, 'id' | 'createdAt'>, extras: AddMatterExtras) => Matter;
  /** Update a matter — optimistic UI update, then sync to API */
  /**
   * Save an edit. Resolves TRUE when the server accepted it, FALSE when it refused (the local
   * state has been rolled back and the reason already shown).
   *
   * Callers must not announce success before this resolves: the write is optimistic, so a
   * refusal — "cannot archive a matter with active hearings scheduled" — arrived AFTER a
   * cheerful "Matter updated successfully", leaving two contradictory messages on screen and
   * the user believing a change that had been undone.
   */
  updateMatter: (id: string, patch: Partial<Matter>) => Promise<boolean>;
  /** Delete a matter — optimistic UI update, then sync to API */
  deleteMatter: (id: string) => void;
}

export const useMattersStore = create<MattersState>()((set, get) => ({
  matters: [],
  loading: false,
  error: null,
  truncated: false,

  fetchMatters: async () => {
    set({ loading: true, error: null });
    try {
      const { matters, truncated } = await mattersApi.fetchMatters();
      set({ matters, truncated, loading: false });
    } catch (err) {
      // If API fails (e.g. not logged in), keep empty list
      console.error('Failed to fetch matters:', err);
      set({ loading: false, error: 'Failed to load matters' });
    }
  },

  addMatter: (input, extras) => {
    // Optimistic: add to local state immediately with temp ID
    const tempMatter: Matter = { ...input, id: `temp-${Date.now()}`, createdAt: Date.now() };
    set((s) => ({ matters: [tempMatter, ...s.matters] }));

    // Fire API call in background, then replace temp matter with real one
    mattersApi.createMatter({
      caseName: input.name,
      status: input.status,
      // No 'other' fallback: an unanswered case type must fail loudly, not be mis-filed.
      caseType: extras.caseType,
      description: input.summary,
      internalNotes: input.internalNotes,
      feeType: (input.feeType || undefined) as any,
      feeEstimate: input.feeEstimate || undefined,
      priority: extras?.priority,
      courtCaseNumber: extras?.courtCaseNumber,
      partyA: extras?.partyA,
      partyB: extras?.partyB,
      instructingSolicitorId: extras?.instructingSolicitorId,
      instructingSolicitor: extras?.instructingSolicitor,
      leadBarristerId: extras?.leadBarristerId,
      juniorBarristerId: extras?.juniorBarristerId,
      courtId: extras?.courtId,
      practiceAreaId: extras?.practiceAreaId,
      isHistoricalMatter: extras?.isHistoricalMatter,
      originalFilingDate: extras?.originalFilingDate,
      caseStage: extras?.caseStage,
      previousAdvocateName: extras?.previousAdvocateName,
      previousAdvocateFirm: extras?.previousAdvocateFirm,
      conflictOverrideReason: extras?.conflictOverrideReason,
    }).then((realMatter) => {
      set((s) => ({
        matters: s.matters.map((m) => m.id === tempMatter.id ? realMatter : m),
      }));
      extras?.onSuccess?.(realMatter);
    }).catch((err) => {
      console.error('Failed to create matter:', err);
      set((s) => ({ matters: s.matters.filter((m) => m.id !== tempMatter.id) }));
      if (extras?.onError) {
        const status = err?.response?.status;
        const message = err?.response?.data?.error?.message ?? err?.response?.data?.message ?? err?.message ?? 'Failed to create matter';
        extras.onError({ status, message });
      }
    });

    return tempMatter;
  },

  updateMatter: (id, patch) => {
    // Snapshot before mutation for reliable local rollback (no network dependency)
    const previous = get().matters.find((m) => m.id === id);

    // Optimistic: update local state immediately
    set((s) => ({
      matters: s.matters.map((m) => m.id === id ? { ...m, ...patch, updatedAt: Date.now() } : m),
    }));

    // Sync to API in background
    const apiPatch: Record<string, unknown> = {};
    if (patch.name !== undefined) apiPatch.caseName = patch.name;
    if (patch.status !== undefined) apiPatch.status = patch.status;
    if (patch.summary !== undefined) apiPatch.description = patch.summary;
    if (patch.internalNotes !== undefined) apiPatch.internalNotes = patch.internalNotes;
    if (patch.feeEstimate !== undefined) apiPatch.feeEstimate = patch.feeEstimate;
    if (patch.feeType !== undefined) apiPatch.feeType = patch.feeType || undefined;

    // riskRating → priority (normalise legacy display values to API enum)
    if (patch.riskRating !== undefined) {
      const priorityMap: Record<string, string> = {
        low: 'low', normal: 'normal', high: 'high', urgent: 'urgent',
        Low: 'low', Normal: 'normal', High: 'high', Urgent: 'urgent',
        Medium: 'normal', Critical: 'urgent',
      };
      apiPatch.priority = priorityMap[patch.riskRating] ?? 'normal';
    }

    // IDs — passed through when the edit form provides them
    if (patch.courtId !== undefined) apiPatch.courtId = patch.courtId || undefined;
    if (patch.judgeId !== undefined) apiPatch.judgeId = patch.judgeId || undefined;
    if (patch.practiceAreaId !== undefined) apiPatch.practiceAreaId = patch.practiceAreaId || undefined;
    // Lead barrister reassignment — null clears the assignment, a uuid reassigns.
    if (patch.leadBarristerId !== undefined) apiPatch.leadBarristerId = patch.leadBarristerId || null;
    if (patch.visibility !== undefined) apiPatch.visibility = patch.visibility;

    if (Object.keys(apiPatch).length === 0) return Promise.resolve(true);

    return mattersApi.updateMatter(id, apiPatch)
      .then(() => true)
      .catch((err) => {
        console.error('Failed to update matter:', err);
        // Local rollback first — guaranteed even if network is completely down
        if (previous) {
          set((s) => ({ matters: s.matters.map((m) => m.id === id ? previous : m) }));
        }
        // SAY WHAT THE SERVER SAID. A refusal here is usually a rule, not a glitch — "a matter
        // cannot advance to active while a conflict review is pending", "that status change is
        // not allowed", "reopen it first". "Please try again" threw all of that away and invited
        // the user to repeat an action that can never succeed.
        import('sonner').then(({ toast }) => {
          toast.error(getApiErrorMessage(err, 'Could not save the matter — please try again'));
        });
        // Best-effort server re-sync in background
        get().fetchMatters();
        return false;
      });
  },

  deleteMatter: (id) => {
    // Optimistic: remove from local state immediately
    const removed = get().matters.find((m) => m.id === id);
    set((s) => ({ matters: s.matters.filter((m) => m.id !== id) }));

    // Sync to API in background
    mattersApi.deleteMatter(id).catch((err) => {
      console.error('Failed to delete matter:', err);
      // Restore on failure
      if (removed) {
        set((s) => ({ matters: [removed, ...s.matters] }));
      }
    });
  },
}));
